import React, { ReactElement, ReactNode } from 'react'

import { useIntl } from 'react-intl'

import { Autocomplete, MenuItem } from '@mui/material'

import ArrowRightIcon from 'app/assets/icons/arrow-right.svg'
import LocationIcon from 'app/assets/icons/location.svg'
import Box from 'app/components/base/Box'
import Stack from 'app/components/base/Stack'
import Text from 'app/components/base/Text/Text.web'
import TextInput, {
  TextInputProps
} from 'app/components/base/TextInput/TextInput.web'
import useGeosuggestInput, {
  UseGeosuggestInput
} from 'app/hooks/useGeosuggestInput'
import useModal from 'app/hooks/useModal'
import { Suggest } from 'app/services/Geosuggest'
import { LOQATE_ITEM_TYPES } from 'app/services/Geosuggest/Geosuggest.types'
import { isOfType } from 'app/utils/helpers/type.helpers'

const getOptionLabel = (option): string =>
  typeof option === 'string' ? option : `${option.label}, ${option.description}`

const filterOptions = (suggestedOption: (string | Suggest)[]) => suggestedOption

interface CustomProps {
  id?: string
  required?: boolean
  disabled?: boolean
  onChange: UseGeosuggestInput['onChange']
  onDelete?: () => void
  value?: UseGeosuggestInput['value']
  options?: UseGeosuggestInput['options']
  resetInputAfterSelect?: UseGeosuggestInput['resetInputAfterSelect']
}

export type GeosuggestInputProps = Omit<TextInputProps, 'onChange'> &
  CustomProps

const GeosuggestInput: RFC<GeosuggestInputProps> = ({
  value,
  onChange,
  options = {},
  required,
  disabled,
  id,
  label,
  resetInputAfterSelect,
  size = 'small',
  ...rest
}) => {
  const { formatMessage } = useIntl()
  const { open, hideModal, showModal } = useModal()

  const { suggestOptions, onChangeInput, onSelect, inputValue } =
    useGeosuggestInput({
      options,
      onChange,
      resetInputAfterSelect,
      value,
      onAfterSelect: () => hideModal()
    })

  return (
    <Autocomplete
      id={id}
      open={open}
      onOpen={showModal}
      onClose={hideModal}
      getOptionLabel={getOptionLabel}
      filterOptions={filterOptions}
      options={suggestOptions}
      autoComplete
      includeInputInList
      freeSolo
      disableCloseOnSelect
      onChange={(_e, value) => onSelect(isOfType.object(value) ? value : null)}
      value={inputValue}
      disabled={disabled}
      renderInput={(params): ReactElement => {
        return (
          <TextInput
            label={label || formatMessage({ defaultMessage: 'Type Address' })}
            onChange={(event) => onChangeInput(event.target.value)}
            {...params}
            {...rest}
            size={size}
            inputProps={{
              'data-required': required,
              ...params?.inputProps,
              ...rest?.inputProps
            }}
            InputProps={{
              ...params?.InputProps,
              ...rest?.InputProps,
              endAdornment: inputValue && params?.InputProps.endAdornment
            }}
          />
        )
      }}
      renderOption={(props, option: string | Suggest): ReactNode => {
        if (isOfType.string(option)) return null

        const isContainer = option.loqateType !== LOQATE_ITEM_TYPES.address
        const optionKey = option.loqateId ?? option.description

        return (
          <MenuItem
            {...props}
            {...(!!optionKey && { key: optionKey })}
            sx={{ whiteSpace: 'normal' }}
          >
            <Stack
              direction="row"
              spacing={1.5}
              sx={{ alignItems: 'center', width: '100%' }}
            >
              <Box sx={{ width: 24, height: 24 }}>
                <LocationIcon />
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Text
                  variant="body2"
                  lineClamp={3}
                  sx={{ fontWeight: 400, wordBreak: 'break-word' }}
                >
                  {option.label}
                </Text>

                <Text
                  variant="subtitle1"
                  noWrap
                  sx={{ color: 'text.secondary', flex: 1 }}
                >
                  {option.description}
                </Text>
              </Box>

              {isContainer && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 24,
                    height: 24
                  }}
                >
                  <ArrowRightIcon />
                </Box>
              )}
            </Stack>
          </MenuItem>
        )
      }}
    />
  )
}

export default GeosuggestInput
